﻿import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types'
import classNames from 'classnames';
import { Intent } from "@blueprintjs/core";
import { DateRangeInput } from "@blueprintjs/datetime";
import { Button } from "components/buttons";
import { FormHelperText } from "components/form-fields";
import { ShowHide } from "components/layout";
import moment from "moment";
import 'components/form-fields/FormFields.css';
import "moment/locale/en-gb";
import { enGB } from "date-fns/locale";

const dateLocale = "en-gb";
const dateFormat = "DD/MM/YYYY";
// Strict, four-digit years only, so a partly typed date is never read as a whole one; single-digit days and months are still accepted.
const parseFormats = ["DD/MM/YYYY", "D/M/YYYY"];

// Trimmed and strict; bad text still parses to an Invalid Date so the input shows its error.
function parseTypedDate(text) {
    if (text == null) {
        return null;
    }

    return moment(text.trim(), parseFormats, true).locale(dateLocale).toDate();
}

function isWholeDate(value) {
    return value != null && moment(value).isValid();
}

export function FormDateRangeInput(
    {
        id = null,
        startDate = null,
        endDate = null,
        disabled = false,
        loading = false,
        minDate = null,
        maxDate = null,
        headingText = "",
        dangerHelperText = "",
        modifiers = null,
        initialMonthOffset = 0,
        large = false,
        allowClear = true,
        firstDayOfWeek = 1,
        helperText,
        onChange
    }
) {
    const [dates, setDates] = useState({
        startDate: null,
        endDate: null
    });
    const monthsInPastDefault = 30;
    const yearsInFutureDefault = 5;
    const minDateDefault = moment().subtract(monthsInPastDefault, 'months').toDate();
    const maxDateDefault = moment().add(yearsInFutureDefault, 'years').toDate();
    const [minDateValue, setMinDate] = useState(minDateDefault);
    const [maxDateValue, setMaxDate] = useState(maxDateDefault);

    const [initialMonth, setInitialMonth] = useState(moment().add(initialMonthOffset, 'months'));

    const startInputRef = useRef(null);
    const endInputRef = useRef(null);


    useEffect(() => {
        moment.updateLocale(dateLocale, {
            week: {
                dow: firstDayOfWeek, // defaults to monday
            }
        });

    }, [firstDayOfWeek]);
    function setMinDateFromProps() {
        var newMinDate = minDate || minDateDefault;
        if (initialMonth.isBefore(moment(newMinDate))) {
            setInitialMonth(moment(newMinDate));
        }

        if (disabled) {
            newMinDate = moment('1990-01-01').toDate();
        }

        setMinDate(newMinDate);
    }
    function setMaxDateFromProps() {
        var newMaxDate = maxDate || maxDateDefault;
        if (initialMonth.isAfter(moment(newMaxDate))) {
            setInitialMonth(moment(newMaxDate));
        }

        if (disabled) {
            newMaxDate = moment('3000-01-01').toDate();
        }


        setMaxDate(newMaxDate);
    }
    useEffect(setMinDateFromProps, [minDate]);
    useEffect(setMaxDateFromProps, [maxDate]);

    useEffect(() => {
        setDates({
            startDate: startDate ? moment(startDate).locale(dateLocale).toDate() : null,
            endDate: endDate ? moment(endDate).locale(dateLocale).toDate() : null
        });
    }, [startDate, endDate]);

    function isRefused(date, acceptedDate) {
        if (date == null) {
            return false;
        }

        const dateMoment = moment(date);
        if (acceptedDate != null && dateMoment.isSame(acceptedDate, 'day')) {
            return false;
        }

        return !dateMoment.isValid()
            || dateMoment.isBefore(minDateValue, 'day')
            || dateMoment.isAfter(maxDateValue, 'day');
    }

    function handleDateChange(selectedDate) {
        // Blueprint's Enter key hands over a typed date without its own min/max check. A bound the
        // user left alone is let through, so a stored out-of-range date cannot block edits to the other.
        if (isRefused(selectedDate[0], dates.startDate) || isRefused(selectedDate[1], dates.endDate)) {
            return;
        }

        let startDateValue = {
            date: null,
            dateString: null
        };

        let endDateValue = {
            date: null,
            dateString: null
        }

        let selectedStart = null;
        let selectedEnd = null;

        if (isWholeDate(selectedDate[0])) {
            const startDateMoment = moment(selectedDate[0]);
            selectedStart = selectedDate[0];
            startDateValue.date = startDateMoment;
            startDateValue.dateString = startDateMoment.format("YYYY-MM-DD");
        }

        if (isWholeDate(selectedDate[1])) {
            const endDateMoment = moment(selectedDate[1]);
            selectedEnd = selectedDate[1];
            endDateValue.date = endDateMoment;
            endDateValue.dateString = endDateMoment.format("YYYY-MM-DD");
        }

        setDates({
            startDate: selectedStart,
            endDate: selectedEnd
        });

        onChange({
            startDate: startDateValue,
            endDate: endDateValue
        });
    }

    function openCalendar(ref) {
        if (ref.current != null) {
            ref.current.focus();
        }
    }

    function receiveStartInputRef(ref) {
        startInputRef.current = ref;
    }

    function receiveEndInputRef(ref) {
        endInputRef.current = ref;
    }

    function clearDates() {
        handleDateChange([null, null]);
    }

    function renderButton(ref) {
        return (
            <Button
                icon="calendar"
                minimal={true}
                onClick={() => { openCalendar(ref) }}
            />
        );
    }

    return (
        <div className="form-field form-field-date-range-input">
            <ShowHide
                evaluator={headingText?.length > 0}
                show={(
                    <h4 className={classNames({ "bp6-skeleton": loading })}>{headingText}</h4>
                )}
            />
            <FormHelperText className={classNames({ "bp6-skeleton": loading })} >{helperText}</FormHelperText>
            <DateRangeInput
                id={id}
                allowSingleDayRange={true}
                contiguousCalendarMonths={false}
                className={classNames({ "bp6-skeleton": loading })}
                formatDate={date => moment(date).locale(dateLocale).format(dateFormat)}
                onChange={handleDateChange}
                parseDate={parseTypedDate}
                initialMonth={initialMonth.toDate()}
                value={[dates.startDate, dates.endDate]}
                startInputProps={{ className: "small", inputRef: receiveStartInputRef, rightElement: renderButton(startInputRef), large: large }}
                endInputProps={{ className: "small", inputRef: receiveEndInputRef, rightElement: renderButton(endInputRef), large: large }}
                disabled={disabled}
                minDate={minDateValue}
                maxDate={maxDateValue}
                dayPickerProps={modifiers?.disabledDays != null ? { disabled: modifiers.disabledDays } : undefined}
                locale={enGB}
            />
            <ShowHide
                evaluator={dates.startDate != null && dates.endDate != null && allowClear}
                show={(
                    <Button text="Clear dates" id={"clear-dates-btn" + id} intent={Intent.DANGER} minimal={true} icon="cross" onClick={clearDates} disabled={disabled} large={large}></Button>
                )}
            ></ShowHide>
            <FormHelperText loading={loading} danger={true}>{dangerHelperText}</FormHelperText>
        </div>
    );
}

FormDateRangeInput.propTypes = {
    id: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
    loading: PropTypes.bool,
    headingText: PropTypes.string,
    disabled: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    modifiers: PropTypes.object,
    dangerHelperText: PropTypes.string,
    initialMonthOffset: PropTypes.number,
    large: PropTypes.bool,
    allowClear: PropTypes.bool,
    firstDayOfWeek: PropTypes.number //This must be set consitently on a page. You cannot have on dateInput starting on different days of the week on the same page 
};